<i18n src="./translations"></i18n>

<template>
  <Dialog
    ref="alternative-products-dialog"
    class="alternative-products-dialog"
    modifier="alternative-products-dialog"
    :title="dialogTitle"
    :active="active"
    @hide="closeAction"
  >
    <div class="alternative-products-dialog-container">
      <!-- Receives the focus when the dialog opens -->
      <div
        ref="content"
        class="content"
        tabindex="-1"
        role="group"
        :aria-label="dialogTitle"
      >
        <div v-if="isParentAvailable()" class="intro">
          {{ $t('alternative-products-dialog.description') }}
          <PdpAvailability
            v-if="parentAvailability"
            class="intro-delivery-time"
            :state="parentAvailability"
          />
        </div>
        <strong class="suggestion-title">
          {{ $t('alternative-products-dialog.suggestion-title') }}
        </strong>
        <ul class="benefits">
          <li>
            <Icon name="checkmark_green" />
            <span>
              {{ $t('alternative-products-dialog.available_immediately') }}
            </span>
          </li>
          <li v-if="vehicleName">
            <Icon name="checkmark_green" />
            <span>
              {{ $t('alternative-products-dialog.compatible_with')
              }}{{ vehicleName }}
            </span>
          </li>
          <li>
            <Icon name="checkmark_green" />
            <span>
              {{ $t('alternative-products-dialog.similar_price_range') }}
            </span>
          </li>
        </ul>
        <div v-if="product" class="product-container">
          <a
            class="product-title"
            :href="product.productUrl"
            @click="onProductTitleClick"
            >{{ product.name }}</a
          >
          <div class="product-details">
            <div class="image-container">
              <AdvancedImage
                v-if="product.images && product.images.length > 0"
                class="alternative-products-dialog-product-image"
                :src="product.images[0].src"
                :alt="product.images[0].alt || product.name"
                :config="{ q: 80, w: 300 }"
              />
            </div>
            <div class="price-container">
              <PriceInfo21
                ref="priceInfo"
                modifier="slim"
                :product="product"
                :availability-enabled="false"
              />
            </div>
            <div v-if="product.brandLogoUrl" class="brand-logo-container">
              <AdvancedImage
                class="alternative-products-dialog-brand-image"
                :src="product.brandLogoUrl"
                :alt="product.brandName || ''"
                :config="{ q: 80, w: 100 }"
              />
            </div>
          </div>
          <Button
            v-if="!isParentAvailable()"
            class="confirm-button"
            :on-click="confirmAction"
            :title="$t('alternative-products-dialog.confirm-title_unavailable')"
            :modifiers="['primary']"
          />
        </div>
        <p v-if="!isParentAvailable() && showPhoneHint" class="phone-hint">
          {{
            $t('alternative-products-dialog.phone-hint_unavailable', {
              '%customerServicePhoneNumber%': customerServicePhoneNumber,
            })
          }}
        </p>
      </div>
      <div v-if="isParentAvailable()" class="footer">
        <div class="buttons-container">
          <Button
            v-for="button in footerButtons"
            :key="button.name"
            :class="`${button.name}-button`"
            :on-click="button.onClick"
            :title="$t(`alternative-products-dialog.${button.name}-title`)"
            :modifiers="[button.modifier]"
          />
        </div>
      </div>
    </div>
  </Dialog>
</template>

<script>
import AdvancedImage from 'Components/01-atoms/advanced-image/AdvancedImage';
import Dialog from 'Components/02-molecules/dialog/Dialog';
import globalMixin from 'Libs/mixins/globalMixin';
import { mapActions, mapGetters, mapState } from 'vuex';
import { axios } from 'Services/AjaxService';
import Icon from 'Components/00-generated/Icon';
import PdpAvailability, {
  STATUS_SOON,
  STATUS_SYNONYMS,
} from 'Components/01-atoms/pdp-availability/PdpAvailability';
import PriceInfo21 from 'Components/02-molecules/price-info-21/PriceInfo21.vue';
import Button from 'Components/01-atoms/button/Button.vue';
import { UNAVAILABLE_AVAILABILITY_STATUSES } from 'Store/constants/availability';

const openDialogs = [];

export default {
  name: 'AlternativeProductsDialog',
  components: {
    Dialog,
    AdvancedImage,
    Icon,
    PdpAvailability,
    PriceInfo21,
    Button,
  },
  mixins: [globalMixin],
  props: {
    parentProduct: {
      type: Object,
      default: () => {},
    },
    handleConfirm: {
      type: Function,
      default: () => {},
    },
    handleCancel: {
      type: Function,
      default: () => {},
    },
  },
  data() {
    return {
      product: null,
      active: false,
    };
  },
  computed: {
    ...mapState('core', {
      salesChannelId: ({ salesChannel }) => salesChannel.salesChannelId,
      hideSupportPhoneNumber: ({ salesChannel }) =>
        salesChannel.config.hideSupportPhoneNumber,
    }),
    ...mapGetters('core', ['customerServicePhoneNumber', 'isMobileViewport']),
    ...mapState('carSelection', {
      carTypeNumber: ({ data: { vehicle = {} } }) => {
        const { ktypnr } = vehicle;
        return ktypnr ?? null;
      },
      vehicleName: ({ data: { vehicle = {} } }) => {
        const { manufacturer, model, type } = vehicle;
        return manufacturer && model && type
          ? `${manufacturer} ${model} ${type}`
          : null;
      },
    }),

    alternativeProductsAPIUrl() {
      return this.frontFacingPath('search.alternativeproducts.get');
    },
    footerButtons() {
      const buttons = [
        { name: 'cancel', onClick: this.cancelAction, modifier: 'secondary' },
        { name: 'confirm', onClick: this.confirmAction, modifier: 'primary' },
      ];

      // DOM order follows the visual order so keyboard and screen reader
      // navigation match the layout: primary on top on mobile, right on desktop
      return this.isMobileViewport ? buttons.reverse() : buttons;
    },
    dialogTitle() {
      return this.$t(
        `alternative-products-dialog.title${this.translationSuffix}`
      );
    },
    translationSuffix() {
      return this.isParentAvailable() ? '' : '_unavailable';
    },
    parentAvailability() {
      const availability = this.parentProduct?.availability;
      if (typeof availability !== 'string') {
        return '';
      }

      // Only availabilities with a delivery time label ("Lieferzeit: ...")
      const status = STATUS_SYNONYMS[availability.toLowerCase()];
      return STATUS_SOON.has(status) ? availability : '';
    },
    showPhoneHint() {
      return !!this.customerServicePhoneNumber && !this.hideSupportPhoneNumber;
    },
  },
  created() {
    // Element that had the focus before the dialog opened (not reactive)
    this.focusReturnElement = null;
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.handleKeydown);
    this.removeFromOpenDialogs();
  },
  methods: {
    ...mapActions('dataLayer', [
      'trackAlternativeProductOpened',
      'trackAlternativeProductConfirm',
      'trackAlternativeProductCancel',
      'trackAlternativeProductDetailPageOpened',
    ]),
    async fetchAlternativeProduct(productId, quantity) {
      const payload = {
        product_id: productId,
        quantity: quantity,
        cartype_number: this.carTypeNumber,
      };

      let response = await axios.post(this.alternativeProductsAPIUrl, payload);

      if (
        response.success &&
        response.data.length > 0 &&
        response.data[0].searchResult
      ) {
        return response.data[0].searchResult;
      }

      return null;
    },
    showDialog(product) {
      if (!this.active) {
        this.focusReturnElement = document.activeElement;
      }
      this.product = product;
      this.active = true;
      this.$refs['alternative-products-dialog'].show();
      document.addEventListener('keydown', this.handleKeydown);
      if (!openDialogs.includes(this)) {
        openDialogs.push(this);
      }
      this.focusContent();
      this.trackAlternativeProductOpened(this.parentProduct, this.product);
    },
    hideDialog() {
      this.active = false;
      this.$refs['alternative-products-dialog'].hide();
      this.endKeyboardInteraction();
    },
    focusContent() {
      // The content is rendered through a portal, which updates one tick after the dialog
      this.$nextTick(() => {
        this.$nextTick(() => {
          if (this.active && this.$refs.content) {
            this.$refs.content.focus();
          }
        });
      });
    },
    handleKeydown(event) {
      const isTopmost = openDialogs[openDialogs.length - 1] === this;

      if (event.key === 'Escape' && this.active && isTopmost) {
        // Same path as the close button and the outside click
        this.$refs['alternative-products-dialog'].hide();
      }
    },
    endKeyboardInteraction() {
      document.removeEventListener('keydown', this.handleKeydown);
      this.removeFromOpenDialogs();

      const element = this.focusReturnElement;
      this.focusReturnElement = null;

      if (
        element &&
        element !== document.body &&
        document.contains(element) &&
        typeof element.focus === 'function'
      ) {
        element.focus();
      }
    },
    removeFromOpenDialogs() {
      const index = openDialogs.indexOf(this);
      if (index !== -1) {
        openDialogs.splice(index, 1);
      }
    },
    confirmAction() {
      if (this.active) {
        // Prevent multiple calls
        const productToReturn = {
          ...this.product.searchResult,
          ...this.product,
        };

        this.handleConfirm(productToReturn);
        this.hideDialog();
        this.trackAlternativeProductConfirm(this.parentProduct, this.product);
      }
    },
    cancelAction() {
      if (this.active) {
        // Prevent multiple calls
        this.handleCancel();
        this.hideDialog();
        this.trackAlternativeProductCancel(this.parentProduct, this.product);
      }
    },
    closeAction() {
      if (this.active) {
        // Prevent multiple calls
        this.active = false;
        this.handleCancel();
        this.trackAlternativeProductCancel(this.parentProduct, this.product);
        this.endKeyboardInteraction();
      }
    },
    onProductTitleClick(event) {
      // Opening the product in a new tab or window is left to the browser
      if (event.ctrlKey || event.metaKey || event.shiftKey) {
        this.trackAlternativeProductDetailPageOpened(
          this.parentProduct,
          this.product
        );
        return;
      }

      event.preventDefault();
      this.openProduct();
    },
    openProduct() {
      this.trackAlternativeProductDetailPageOpened(
        this.parentProduct,
        this.product
      ).finally(() => {
        if (this.product.productUrl) {
          window.location.href = this.product.productUrl;
        }
      });
    },
    isParentAvailable() {
      return (
        this.parentProduct && //Exists
        this.parentProduct.prices.price > 0 && //Has a price
        !UNAVAILABLE_AVAILABILITY_STATUSES.includes(
          (this.parentProduct.availability ?? '').toUpperCase()
        ) //Is available
      );
    },
  },
};
</script>

<style lang="scss">
@import 'variables/breakpoints.scss';

// Sizes follow the Figma handoff of DEV-57748 (Alternative product modal)
.dialog.dialog--alternative-products-dialog {
  // The modal grows with its content up to the viewport height, only the
  // content area scrolls when it does not fit. Header and footer stay visible.
  .frame {
    display: flex;
    flex-direction: column;
    max-height: calc(100vh - 40px);
    border-radius: 3px;

    @supports (height: 100dvh) {
      max-height: calc(100dvh - 40px);
    }
  }

  .dialogFrame {
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    min-height: 0;
    width: 100%;

    article {
      flex: 1 1 auto;
      min-height: 0;
      height: auto;
    }

    .header {
      flex-shrink: 0;
      min-height: 62px;
      padding: 0 var(--space-2) 0 23px;
    }

    .closeButton {
      width: 24px;
      height: 24px;
      padding: 0;
      border: 0;
      border-radius: 0;
      background: transparent;
      color: var(--color-nero);

      svg {
        display: block;
        margin-top: 0;
      }
    }

    .body {
      display: flex;
      flex: 1 1 auto;
      min-height: 0;
      max-height: none;
      overflow: hidden;
      border-top-color: var(--color-kfzteile24-gray);
    }
  }

  .slotFrame {
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
    min-height: 0;
    overflow: hidden;
    padding: 0;
  }

  .alternative-products-dialog-container {
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
    min-height: 0;
    overflow: hidden;
    color: var(--color-nero);
    font-size: var(--font-size-M);
    line-height: 20px;

    .content {
      display: flex;
      flex: 1 1 auto;
      flex-direction: column;
      gap: var(--space-2);
      min-height: 0;
      overflow-y: auto;
      padding: var(--space-2);

      > * {
        flex-shrink: 0;
      }

      // Focused programmatically when the dialog opens, it is not a control
      &:focus {
        outline: none;
      }
    }

    // Very short viewports (landscape phones, high zoom levels): the footer
    // scrolls together with the content so it can never be clipped
    @media (max-height: 500px) {
      overflow-y: auto;

      .content {
        flex: none;
        overflow: visible;
      }
    }

    .intro-delivery-time {
      display: block;
      font-size: inherit;
      line-height: inherit;

      .label {
        display: inline;
        color: inherit;
        line-height: inherit;

        .icon {
          display: none;
        }
      }
    }

    .suggestion-title {
      display: block;
      font-weight: 700;
    }

    .benefits {
      display: flex;
      flex-direction: column;
      gap: var(--space-1);

      li {
        display: flex;
        align-items: center;
      }

      svg {
        flex-shrink: 0;
        width: 20px;
        height: 20px;
        margin-right: var(--space-1);
      }
    }

    .phone-hint {
      margin: 0;
      line-height: 18px;
    }

    .footer {
      flex-shrink: 0;
      padding: var(--space-2);
      border-top: solid 1px var(--color-kfzteile24-gray);
      background: var(--color-white);
    }

    // Button order comes from footerButtons, the same breakpoint as the
    // isMobileViewport getter (mobile up to $_widthMTo) is used for the layout
    .buttons-container {
      display: flex;
      flex-direction: column;
      gap: var(--space-2);

      .button {
        width: 100%;
        max-width: none;
        min-height: 48px;

        .inner {
          padding: 0 var(--space-3);
        }
      }

      // Desktop: right aligned row, secondary action left of the primary action
      @media #{$_mediaLUp} {
        flex-direction: row;
        justify-content: flex-end;

        .button {
          width: auto;
        }
      }
    }
  }

  .product-container {
    padding: var(--space-2);
    border: solid 1px var(--color-kfzteile24-gray);
    border-radius: var(--space-1);
    background: var(--color-white);

    .product-title {
      display: block;
      margin-bottom: var(--space-2);
      color: inherit;
      font-size: var(--font-size-ML);
      font-weight: 700;
      line-height: 24px;
      text-decoration: none;
      cursor: pointer;
      overflow-wrap: anywhere;

      &:hover {
        text-decoration: underline;
      }
    }

    .product-details {
      position: relative;
      display: grid;
      grid-template-columns: minmax(0, 2fr) minmax(0, 3fr);
      gap: var(--space-2);
      align-items: start;
    }

    .image-container {
      height: 151px;
    }

    .alternative-products-dialog-product-image,
    .alternative-products-dialog-brand-image {
      height: 100%;

      picture {
        display: block;
        height: 100%;
      }
    }

    .brand-logo-container {
      position: absolute;
      top: 0;
      left: 0;
      width: 56px;
      height: 40px;
      padding: var(--space-0--half);
      border-radius: 3px;
      background: var(--color-white);
    }

    .price-container {
      min-width: 0;
    }

    .priceInfo21 {
      gap: var(--space-1--half);
    }

    .price-tag {
      .display-price {
        line-height: 32px;

        strong {
          font-size: 28px;
          line-height: 32px;
        }
      }

      .retail-price {
        margin-top: 2px;
        font-size: var(--font-size-S);
        line-height: 16px;
      }

      .price-details {
        margin: var(--space-1) 0 0;
        color: var(--color-charcoal-gray);
        font-size: var(--font-size-S);
        line-height: 16px;
      }
    }

    .priceTexts .priceText {
      margin: 0;
      color: var(--color-charcoal-gray);
      font-size: var(--font-size-S);
      line-height: 16px;
    }

    .confirm-button {
      width: 100%;
      max-width: none;
      min-height: 46px;
      margin-top: var(--space-3);
    }

    .wishlistButton {
      display: none !important;
    }

    .mainPrice {
      color: var(--color-black);
      margin-bottom: var(--space-2);
    }
  }
}

// The kfzteile24 theme sets the dialog title to 20/24 in DialogFrame,
// this modal uses 16/20
.dialog.dialog--alternative-products-dialog {
  .dialogFrame article .header,
  :root[data-theme] & .dialogFrame article .header {
    font-size: var(--font-size-M);
    font-weight: 700;
    line-height: 20px;
  }
}
</style>
